class ShopQuestTickets extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  attendeesForm = null;

  /** @type {Array<{id: number, price: number, quantity: number, category: string, properties: Record<string, string>}>} */
  tickets = [];

  constructor() {
    super();

    this.config = this.getConfig();
    this.bundleId = this.config["bundle_id"];
    this.bundleVariantId = this.config.options[0]["variant_id"];
    this.addToCartButton = this.querySelector('#add-to-cart-button');

    const attendeesForm = this.querySelector('.attendees__form');
    if ((attendeesForm instanceof HTMLFormElement)) {
      this.attendeesForm = attendeesForm;
      attendeesForm.addEventListener('submit', this.handleAddAttendee);
    }
  }

  connectedCallback() {
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', this.addToCart);
    }

    const viewSummaryButton = this.querySelector('.shop-quest__view-summary-button');
    if (viewSummaryButton) {
      viewSummaryButton.addEventListener('click', this.toggleSummary);
    }
  }

  toggleSummary = () => {
    const summary = this.querySelector('.shop-quest-tickets__tickets-summary');
    const viewSummaryButton = this.querySelector('.shop-quest__view-summary-button');
    if (!summary) return;

    const isOpen = summary.classList.toggle('is-summary-open');
    if (viewSummaryButton) {
      viewSummaryButton.textContent = isOpen ? 'hide summary' : 'view summary';
    }
  }

  getConfig = () => {
    const config = this.querySelector('script[type="application/json"]');
    if(!config) throw new Error('Config not found');
    return JSON.parse(config.textContent);
  }

  /**
   * @param {Event} event
   */
  handleAddAttendee = (event) => {
    event.preventDefault();
    if (!this.attendeesForm) return;

    const formData = new FormData(this.attendeesForm);

    /** @type {Record<string, string>} */
    const properties = {};

    for (const [key, value] of formData.entries()) {
      if (typeof value !== 'string') continue;
      properties[key] = value;
    }

    const mcpOptIn = this.attendeesForm.querySelector('#mcp_opt_in');
    properties._mcp_opt_in =
      mcpOptIn instanceof HTMLInputElement && mcpOptIn.checked ? 'True' : 'False';

    const currentCategory = this.config.categories[0];
    const variantEntry = Object.entries(
      currentCategory.products[0].variants
    )[0];
    if (!variantEntry) return;

    const [variantId, price] = variantEntry;

    this.tickets.push({
      id: Number(variantId),
      price: Number(price),
      quantity: 1,
      category: currentCategory.title,
      properties: properties
    });

    const company = properties.Company;
    const companyType = properties._company_type;
    const website = properties._website;
    const city = properties._city;
    const country = properties.Country;

    this.updateSummary();
    this.attendeesForm.reset();

    /** @type {Array<[string, string | undefined]>} */
    const persistedFields = [
      ['Company', company],
      ['_company_type', companyType],
      ['_website', website],
      ['_city', city],
      ['Country', country],
    ];

    for (const [fieldName, fieldValue] of persistedFields) {
      const field = this.attendeesForm.querySelector(`[name="${fieldName}"]`);
      if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
        field.value = fieldValue || '';
      }
    }
  };

  updateSummary = () => {
    const priceElement = this.querySelector('.shop-quest-tickets__price');
    const summaryGridElement = this.querySelector('.shop-quest-tickets__summary-grid');
    const totalAttendeesElement = this.querySelector('#total-attendees');
    const addToCartButton = this.querySelector('#add-to-cart-button');
    const total = this.tickets.reduce(
      (sum, ticket) => sum + ticket.price * ticket.quantity * 100,
      0
    );

    if (priceElement) {
      // @ts-ignore
      priceElement.textContent = window.foxsell.formatMoney(total);
    }

    if (totalAttendeesElement) {
      totalAttendeesElement.textContent = String(this.tickets.length);
    }

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = this.tickets.length === 0;
    }

    if(!this.tickets.length) {
      if(summaryGridElement) {
        summaryGridElement.innerHTML = '<p>Add attendees to your cart to see the summary here.</p>';
      }
      return;
    };

    if(summaryGridElement) {
      summaryGridElement.innerHTML = '';
    }

    for(const [index, ticket] of this.tickets.entries()) {
      const props = ticket.properties;
      const socialLinks = [
        props._x ? `<a href="${props._x}" class="shop-quest-tickets__social-link" target="_blank" rel="noopener noreferrer">X</a>` : '',
        props._linkedin ? `<a href="${props._linkedin}" class="shop-quest-tickets__social-link" target="_blank" rel="noopener noreferrer">LinkedIn</a>` : '',
        props._website ? `<a href="${props._website}" class="shop-quest-tickets__social-link" target="_blank" rel="noopener noreferrer">Website</a>` : '',
      ].filter(Boolean).join(' · ');

      const ticketElement = document.createElement('div');
      ticketElement.classList.add('shop-quest-tickets__ticket');
      ticketElement.innerHTML = `
          <div class="shop-quest-tickets__ticket-header">
            <strong>#Ticket ${index + 1}: ${props.Name || ''}</strong>
            <button type="button" class="shop-quest-tickets__remove-ticket-button" data-ticket-index="${index}">remove</button>
          </div>
          <div>${props.Email || ''}</div>
          <div>${props._number || ''}</div>
          <div>${props.Role || ''} @ ${props.Company || ''}</div>
          <div>${[props._city, props.Country].filter(Boolean).join(', ')}</div>
          ${props._food_allergens ? `<strong>Dietary restrictions</strong><div>${props._food_allergens}</div>` : ''}
          <div>${socialLinks}</div>
      `;
      const removeButton = ticketElement.querySelector('.shop-quest-tickets__remove-ticket-button');
      if (removeButton) {
        removeButton.addEventListener('click', () => this.removeTicket(index));
      }
      summaryGridElement?.appendChild(ticketElement);
    }
  }

  /**
   * @param {number} index
   */
  removeTicket = (index) => {
    if (index < 0 || index >= this.tickets.length) return;
    this.tickets.splice(index, 1);
    this.updateSummary();
  }

  addToCart = async () => {
    if (!this.tickets.length) return;

    let bundleItems = this.tickets.map(ticket => ({
      'variantId': ticket.id,
      'quantity': ticket.quantity,
      'category': ticket.category,
      'type': 'product',
      'properties': ticket.properties
    }));

    let bundleId = `${this.bundleId}_${Date.now()}`;
    if (this.addToCartButton instanceof HTMLButtonElement) {
      this.addToCartButton.disabled = true;
    }

    try {
      // @ts-ignore
      await fetch(`${window.Shopify.routes.root}cart/clear.js`);

      // @ts-ignore
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: this.bundleVariantId,
            quantity: 1,
            properties: {
              'Tax': '18% Included',
              '__foxsell:dynamic_add_on_bundle_id': bundleId,
              '__foxsell:dynamic_add_on_bundle_items': JSON.stringify(bundleItems),
            },
          }],
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => null);
        const message = error?.description || error?.message || '';
        if (/stock|sold out|unavailable/i.test(message)) {
          throw new Error('Out of stock');
        }
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      if (this.addToCartButton instanceof HTMLButtonElement) {
        this.addToCartButton.disabled = false;
      }
      window.location.reload();
      return;
    }

    // @ts-ignore — locale-aware checkout URL via Shopify.routes.root
    window.location.href = `${window.Shopify.routes.root}checkout`;
  }
}

customElements.define('shop-quest-tickets', ShopQuestTickets);

(() => {
  const nav = document.querySelector('.navigation-menu');
  if (!nav) return;

  /** @type {Map<string, string>} */
  const shortcuts = new Map();

  nav.querySelectorAll('.navigation-menu-item a[href^="#"]').forEach((link) => {
    const match = link.textContent?.match(/\[([A-Za-z])\]/);
    if (!match) return;
    shortcuts.set(match[1].toLowerCase(), link.getAttribute('href') || '');
  });

  document.addEventListener('keydown', (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    const target = event.target;
    if (
      target instanceof HTMLElement &&
      (target.isContentEditable ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT')
    ) {
      return;
    }

    const href = shortcuts.get(event.key.toLowerCase());
    if (!href) return;

    const section = document.querySelector(href);
    if (!(section instanceof HTMLElement)) return;

    event.preventDefault();
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();
